<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  type PropType,
  type Ref,
  watch,
} from "vue";
import { ref } from "vue";
import {
  type FileAnnotation,
  getFileAnnotations,
  putFileAnnotation,
  type Species,
  type UpdateFileAnnotation,
} from "@api/api";
import RecordingAnnotationEditor from "./RecordingAnnotationEditor.vue";
import {
  getNABatRecordingFileAnnotations,
  putNABatFileAnnotation,
} from "@api/NABatApi";
import RecordingAnnotationDetails from "./RecordingAnnotationDetails.vue";
import useState from "@use/useState";
import useNABatTokens from "@/use/useNABatTokens";
import { decodeJWT } from "../use/useJWTToken";
export default defineComponent({
  name: "AnnotationList",
  components: {
    RecordingAnnotationEditor,
    RecordingAnnotationDetails,
  },
  props: {
    species: {
      type: Array as PropType<Species[]>,
      required: true,
    },
    recordingId: {
      type: Number,
      required: true,
    },
    type: {
      type: String as PropType<"nabat" | null>,
      default: () => null,
    },
  },
  emits: [],
  setup(props) {
    const selectedAnnotation: Ref<null | FileAnnotation> = ref(null);
    const annotationState: Ref<"creating" | "editing" | null> = ref(null);
    const addingAnnotation = ref(false);
    const annotations: Ref<FileAnnotation[]> = ref([]);
    const detailsDialog = ref(false);
    const detailRecordingId = ref(-1);
    const { configuration, isNaBat, currentUser } = useState();
    const { nabatApiToken } = useNABatTokens();

    const setSelectedId = (annotation: FileAnnotation) => {
      selectedAnnotation.value = annotation;
    };

    const currentNaBatUser: Ref<string | null> = ref(null);

    const loadFileAnnotations = async () => {
      if (props.type === "nabat") {
        annotations.value = (
          await getNABatRecordingFileAnnotations(props.recordingId)
        ).data;
      } else {
        annotations.value = (await getFileAnnotations(props.recordingId)).data;
      }
    };

    watch(
      () => props.recordingId,
      async () => {
        selectedAnnotation.value = null;
        await loadFileAnnotations();
      },
    );

    onMounted(async () => {
      await loadFileAnnotations();
      if (props.type === "nabat") {
        const decoded = decodeJWT(nabatApiToken.value);
        if (decoded["email"]) {
          currentNaBatUser.value = decoded["email"];
          const foundItem = annotations.value.find(
            (item) => item.owner === currentNaBatUser.value,
          );
          if (foundItem) {
            setSelectedId(foundItem);
          }
        }
      }
    });

    const addAnnotation = async () => {
      addingAnnotation.value = true;
      try {
        const newAnnotation: UpdateFileAnnotation = {
          recordingId: props.recordingId,
          species: [],
          comments: "",
          model: "User Defined",
          confidence: 1.0,
        };
        if (props.type === "nabat") {
          await putNABatFileAnnotation(newAnnotation);
        } else {
          await putFileAnnotation(newAnnotation);
        }
        await loadFileAnnotations();
        if (annotations.value.length) {
          setSelectedId(annotations.value[annotations.value.length - 1]);
        }
      } finally {
        addingAnnotation.value = false;
      }
    };

    const updatedAnnotation = async (deleted = false) => {
      await loadFileAnnotations();
      if (selectedAnnotation.value) {
        const found = annotations.value.find(
          (item) => selectedAnnotation.value?.id === item.id,
        );
        if (found) {
          selectedAnnotation.value = found;
        }
      }
      if (deleted) {
        selectedAnnotation.value = null;
      }
    };

    function handleSubmitAnnotation(
      annotation: FileAnnotation,
      submitSuccess: boolean,
    ) {
      if (submitSuccess) {
        annotation.submitted = true;
      }
    }

    const loadDetails = async (id: number) => {
      detailRecordingId.value = id;
      detailsDialog.value = true;
    };

    const isAdmin = computed(() => configuration.value.is_admin);

    const userSubmittedAnnotationId = computed(() => {
      if (!configuration.value.mark_annotations_completed_enabled) {
        return undefined;
      }
      const userAnnotations = annotations.value.filter(
        (annotation: FileAnnotation) => annotation.owner === currentUser.value,
      );
      const submittedAnnotation = userAnnotations.find(
        (annotation: FileAnnotation) => annotation.submitted,
      );
      return submittedAnnotation?.id;
    });

    const disableNaBatAnnotations = computed(() => {
      const currentUserAnnotations = annotations.value.filter(
        (item) => item.owner === currentNaBatUser.value,
      );
      if (isAdmin.value && props.type === "nabat" && !nabatApiToken.value) {
        return true;
      }
      return currentUserAnnotations.length > 0 && props.type === "nabat";
    });

    const vettingMode = computed(
      () => configuration.value.mark_annotations_completed_enabled,
    );
    // Count all annotations owned by current user (submitted and unsubmitted) for vetting one-annotation limit
    const userAnnotationCount = computed(
      () =>
        annotations.value.filter(
          (a: FileAnnotation) => a.owner === currentUser.value,
        ).length,
    );
    const vettingModeAddDisabled = computed(
      () => vettingMode.value && userAnnotationCount.value > 0,
    );
    const addButtonDisabled = computed(
      () =>
        addingAnnotation.value ||
        disableNaBatAnnotations.value ||
        vettingModeAddDisabled.value,
    );
    const addButtonTooltip = computed(() => {
      if (vettingModeAddDisabled.value) {
        return "In vetting mode you may only add one annotation per recording.";
      }
      return "";
    });

    function getConfidenceLabelText(confidence: number) {
      return `Confidence: ${confidence.toFixed(2)}`;
    }

    return {
      selectedAnnotation,
      annotationState,
      addingAnnotation,
      annotations,
      setSelectedId,
      addAnnotation,
      updatedAnnotation,
      loadDetails,
      getConfidenceLabelText,
      isNaBat,
      detailsDialog,
      detailRecordingId,
      disableNaBatAnnotations,
      currentNaBatUser,
      userSubmittedAnnotationId,
      handleSubmitAnnotation,
      configuration,
      addButtonDisabled,
      addButtonTooltip,
      userAnnotationCount,
      vettingModeAddDisabled,
      vettingMode,
    };
  },
});
</script>

<template>
  <div>
    <v-row class="pa-4">
      <v-col v-if="!isNaBat()" cols="6">
        <v-row> Annotations </v-row>
        <v-row v-if="userSubmittedAnnotationId" class="mt-8">
          <b>File Reviewed</b>
          <v-icon color="success"> mdi-check </v-icon>
        </v-row>
      </v-col>
      <v-spacer />
      <v-col v-if="!isNaBat() || !disableNaBatAnnotations">
        <v-tooltip v-if="addButtonTooltip" location="bottom">
          <template #activator="{ props: tooltipProps }">
            <div v-bind="tooltipProps">
              <v-btn
                :disabled="addButtonDisabled"
                :loading="addingAnnotation"
                @click="addAnnotation()"
              >
                Add<v-icon>mdi-plus</v-icon>
              </v-btn>
            </div>
          </template>
          {{ addButtonTooltip }}
        </v-tooltip>
        <v-btn
          v-else
          :disabled="addButtonDisabled"
          :loading="addingAnnotation"
          @click="addAnnotation()"
        >
          Add<v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-list class="annotation-list">
      <v-list-item
        v-for="annotation in annotations"
        :id="`annotation-${annotation.id}`"
        :key="annotation.id"
        :class="{ selected: annotation.id === selectedAnnotation?.id }"
        :disabled="
          type === 'nabat' &&
          disableNaBatAnnotations &&
          annotation.owner !== currentNaBatUser
        "
        class="annotation-item"
        @click="setSelectedId(annotation)"
      >
        <v-row>
          <v-col class="annotation-owner">
            <div>{{ annotation.owner }}</div>
            <v-btn
              v-if="annotation.hasDetails"
              size="small"
              @click.stop.prevent="loadDetails(annotation.id)"
            >
              Details
            </v-btn>
          </v-col>
          <v-col
            v-if="!configuration.mark_annotations_completed_enabled"
            class="annotation-confidence"
          >
            <span>{{ getConfidenceLabelText(annotation.confidence) }} </span>
          </v-col>
          <v-col class="annotation-model">
            <span>{{ annotation.model }} </span>
          </v-col>
          <v-col v-if="annotation.submitted">
            Submitted
            <v-icon color="success"> mdi-check </v-icon>
          </v-col>
        </v-row>
        <v-row
          v-for="item in annotation.species"
          :key="`${annotation.id}_${item.common_name}`"
          class="ma-0 pa-0"
        >
          <v-col class="ma-0 pa-0">
            <div class="species-name">
              {{ item.species_code || item.common_name }}
            </div>
            <div v-if="item.family" class="species-hierarchy">
              <span> {{ item.family }}</span>
              <span v-if="item.genus">-></span>
              <span v-if="item.genus">{{ item.genus }}</span>
            </div>
          </v-col>
        </v-row>
      </v-list-item>
    </v-list>
    <RecordingAnnotationEditor
      v-if="selectedAnnotation"
      :species="species"
      :recording-id="recordingId"
      :annotation="selectedAnnotation"
      :submitted-annotation-id="userSubmittedAnnotationId"
      :type="type"
      class="mt-4"
      @update:annotation="updatedAnnotation()"
      @delete:annotation="updatedAnnotation(true)"
      @submit:annotation="handleSubmitAnnotation"
    />
    <v-dialog v-model="detailsDialog" width="600">
      <RecordingAnnotationDetails
        :recording-id="detailRecordingId"
        @close="detailsDialog = false"
      />
    </v-dialog>
  </div>
</template>

<style lang="scss" scoped>
.annotation-id {
  cursor: pointer;
  text-decoration: underline;
}

.annotation-owner {
  font-size: 1em;
}

.annotation-confidence {
  font-size: 1em;
}

.annotation-model {
  font-size: 1em;
}

.annotation-item {
  border: 1px solid gray;
}

.species-name {
  font-weight: bold;
  font-size: 1em;
}

.species-hierarchy {
  font-size: 0.75em;
}

.selected {
  background-color: cyan;
}
</style>
